import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import moment from "moment";
import { UI } from "../../../../services/ui.service";
import { SecEventType } from "../../../../domain/sec-events";
import { SecEventsService } from "../sec-events.service";
import { LanguageToggleTarget } from "../../../../domain/language-toggle-target";
import { Subscription } from "rxjs";
import { SecEventCategory } from "../../../../domain/sec-event-category";
import { ShowRepository } from "../../../../domain/show-repository";
import { Show } from "../../../../domain/show";

@Component({
  selector: "evrideo-calendar-expandable",
  templateUrl: "./calendar-expandable.component.html",
  styleUrls: ["./calendar-expandable.component.scss"],
})
export class CalendarExpandableComponent implements OnInit, OnDestroy {
  @Input() secEvents;

  @Input() show: Show;

  @Input() formatTime: (time: moment.Moment) => string;
  @Input() formatDuration: (duration: moment.Duration) => string;
  @Input() toggleSecEvent: (show: Show, eventId: string) => void;
  @Input() isShowPlaying;
  audioTracks;

  subtitles;

  closedCaptions;

  timedEvents;

  secEventType = SecEventType;

  interval;

  onLanguageChangeSubscription: Subscription;
  onFilterSecEventsSubcription: Subscription;
  onDeleteSecEventSubscription: Subscription;
  addNewSecEventSubscription: Subscription;
  addAudioTrackSubscription: Subscription;
  addNewMarkerSubscription: Subscription;

  secEventTypesToFilter: Set<SecEventType> =
    this.secEventsService.onFilterSecEvents.value;

  filteredSecEvents = [];

  addAudioPopupHidden = true;

  popupAudioTracks = ["EN", "FR", "HE", "SP"];

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private ui: UI,
    private secEventsService: SecEventsService,
    private _showRepository: ShowRepository,
  ) {}

  ngOnInit() {
    this.mapSecEvents();

    this.interval = setInterval(() => {
      this.handleRedLineVisibilty();
    }, 1000);

    this.onLanguageChangeSubscription =
      this.secEventsService.onLanguageChange.subscribe((languageData) => {
        if (languageData.showId === this.show.id) {
          this.updateLanguageUI(
            languageData.languageToggleTarget,
            languageData.language,
          );
        }
      });

    this.onFilterSecEventsSubcription =
      this.secEventsService.onFilterSecEvents.subscribe((selectedFilters) => {
        this.secEventTypesToFilter = selectedFilters;

        this.filteredSecEvents = this.secEvents.filter((event) =>
          selectedFilters.has(event.type),
        );
        this.audioTracks = this.filteredSecEvents.filter(
          (event) => event.type === SecEventType.Audio,
        );
        this.subtitles = this.filteredSecEvents.filter(
          (event) => event.type === SecEventType.Subtitles,
        );
        this.closedCaptions = this.filteredSecEvents.filter(
          (event) => event.type === SecEventType.ClosedCaptions,
        );
        this.timedEvents = this.filteredSecEvents.filter(
          (event) =>
            event.type !== SecEventType.Audio &&
            event.type !== SecEventType.Subtitles &&
            event.type !== SecEventType.ClosedCaptions,
        );

        this.changeDetectorRef.detectChanges();
      });

    this.addNewSecEventSubscription =
      this.secEventsService.onAddNewSecEvent.subscribe((data) => {
        if (data.showId !== this.show.id) {
          return;
        }

        this.secEvents.unshift(data.newSecEvent);
        this.timedEvents = this.secEvents.filter(
          (event) =>
            event.type !== SecEventType.Audio &&
            event.type !== SecEventType.Subtitles &&
            event.type !== SecEventType.ClosedCaptions,
        );

        this.changeDetectorRef.markForCheck();
      });

    this.onDeleteSecEventSubscription =
      this.secEventsService.onDeleteSecEvent.subscribe(
        ({ showId, identifier, originalOffset, eventType }) => {
          if (showId !== this.show.id) {
            return;
          }

          switch (eventType) {
            case SecEventCategory.Graphics: {
              this.secEvents = this.secEvents.filter(
                (event) =>
                  event.type !== SecEventType.Graphics ||
                  event.properties.name !== identifier ||
                  event.properties.offset !== originalOffset,
              );
              break;
            }

            case SecEventCategory.FloatingMedia: {
              this.secEvents = this.secEvents.filter(
                (event) =>
                  event.type !== SecEventType.FloatingMedia ||
                  event.properties.id !== identifier ||
                  event.properties.offset !== originalOffset,
              );
              break;
            }

            case SecEventCategory.Signal: {
              this.secEvents = this.secEvents.filter(
                (event) =>
                  ![SecEventType.SCTE, SecEventType.Cuetone].includes(
                    event.type,
                  ) || event.properties.id !== identifier,
              );
              break;
            }
          }
          this.mapSecEvents();
        },
      );

    this.addAudioTrackSubscription =
      this.secEventsService.onAddAudioTrack.subscribe((data) => {
        if (data.showId === this.show.id) {
          this.audioTracks.push({
            type: SecEventType.Audio,
            properties: { language: data.track, enabled: true },
          });
        }
      });
  }

  ngOnDestroy() {
    this.onLanguageChangeSubscription?.unsubscribe();
    this.onFilterSecEventsSubcription?.unsubscribe();
    this.addNewSecEventSubscription?.unsubscribe();
    this.onDeleteSecEventSubscription?.unsubscribe();
    this.addAudioTrackSubscription?.unsubscribe();
    this.addNewMarkerSubscription?.unsubscribe();
  }

  mapSecEvents() {
    this.audioTracks = this.secEvents.filter(
      (event) => event.type === SecEventType.Audio,
    );
    this.subtitles = this.secEvents.filter(
      (event) => event.type === SecEventType.Subtitles,
    );
    this.closedCaptions = this.secEvents.filter(
      (event) => event.type === SecEventType.ClosedCaptions,
    );
    this.timedEvents = this.secEvents.filter(
      (event) =>
        event.type !== SecEventType.Audio &&
        event.type !== SecEventType.Subtitles &&
        event.type !== SecEventType.ClosedCaptions,
    );
  }

  /*async toggleLanguage(language: string, languageToggleTarget: LanguageToggleTarget) {
    try {
      await this.secEventsService.toggleLanguage(this.showId, language, languageToggleTarget);
    } catch (error) {
      console.log(error.message);
      this.ui.notify(this.ui.EVENTS.ShowAlert, {
        title: 'error toggling language',
        message: error.message
      });
    }
  }*/

  handleRedLineVisibilty() {
    const currentTime = moment().unix();
    for (let i = 0; i < this.timedEvents.length; i++) {
      const startTime = moment(this.timedEvents[i].properties.startTime).unix();
      if (startTime >= currentTime && i === 0 && this.isShowPlaying) {
        this.timedEvents[i].properties.redLineVisible = true;
      } else if (
        startTime >= currentTime &&
        moment(this.timedEvents[i - 1]?.properties.startTime).unix() <
          currentTime
      ) {
        this.timedEvents[i].properties.redLineVisible = true;
      } else {
        this.timedEvents[i].properties.redLineVisible = false;
      }
    }
  }

  updateLanguageUI(languageToggleTarget, language) {
    switch (languageToggleTarget) {
      case LanguageToggleTarget.Subtitles:
        const subtitlesLanguageIndex = this.subtitles.findIndex(
          (element, index) => {
            if (element.properties.language === language) {
              return true;
            }
          },
        );
        this.subtitles[subtitlesLanguageIndex].properties.enabled =
          !this.subtitles[subtitlesLanguageIndex].properties.enabled;
        break;
      case LanguageToggleTarget.Audio:
        const trackLanguageIndex = this.audioTracks.findIndex(
          (element, index) => {
            if (element.properties.language === language) {
              return true;
            }
          },
        );
        this.audioTracks[trackLanguageIndex].properties.enabled =
          !this.audioTracks[trackLanguageIndex].properties.enabled;
        break;
      default:
        break;
    }
  }

  onAddAudioTrackPopupSelect(track) {
    this.secEventsService.addAudioTrackFromPopup(this.show.id, track);
  }
}
